import { Injectable, inject } from '@angular/core';
import {
  Storage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  listAll,
} from '@angular/fire/storage';
import { from, Observable, of } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';

export type ThumbnailType = 'definitions' | 'workspaces' | 'sessions';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private storage = inject(Storage);

  /**
   * Upload an image from a URL to Firebase Storage
   * @param imageUrl The URL of the image to download and upload
   * @param type The type of thumbnail (definitions, workspaces, sessions)
   * @param tenantId The tenant ID
   * @param entityId The entity ID (definition ID, workspace ID, or session ID)
   * @param oldThumbnailUrl Optional: URL of old thumbnail to delete
   * @returns Observable of the public download URL
   */
  uploadThumbnailFromUrl(
    imageUrl: string,
    type: ThumbnailType,
    tenantId: string,
    entityId: string,
    oldThumbnailUrl?: string | null,
  ): Observable<string> {
    // First, delete old thumbnail if provided
    const deleteOld$ = oldThumbnailUrl
      ? this.deleteThumbnail(oldThumbnailUrl).pipe(
          catchError(() => of(null)), // Ignore errors when deleting old thumbnail
        )
      : of(null);

    return deleteOld$.pipe(
      switchMap(() => {
        // Download image from URL
        return from(fetch(imageUrl));
      }),
      switchMap((response) => {
        if (!response.ok) {
          throw new Error(`Failed to download image: ${response.statusText}`);
        }
        return from(response.blob());
      }),
      switchMap((blob) => {
        // Generate unique file path
        const timestamp = Date.now();
        const filePath = `thumbnails/${type}/${tenantId}/${entityId}/${timestamp}.jpg`;
        const storageRef = ref(this.storage, filePath);

        // Upload to Firebase Storage
        return from(uploadBytes(storageRef, blob, { contentType: 'image/jpeg' }));
      }),
      switchMap((snapshot) => {
        // Get public download URL
        return from(getDownloadURL(snapshot.ref));
      }),
    );
  }

  /**
   * Delete a thumbnail from Firebase Storage
   * @param thumbnailUrl The public URL of the thumbnail to delete
   * @returns Observable that completes when deletion is done
   */
  deleteThumbnail(thumbnailUrl: string): Observable<void> {
    try {
      // Extract the path from the URL
      // Firebase Storage URLs are like: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{path}?alt=media&token={token}
      const url = new URL(thumbnailUrl);
      const pathMatch = url.pathname.match(/\/o\/(.+)/);
      if (!pathMatch) {
        throw new Error('Invalid thumbnail URL');
      }

      // Decode the path (it's URL encoded)
      const encodedPath = pathMatch[1];
      const decodedPath = decodeURIComponent(encodedPath);

      const storageRef = ref(this.storage, decodedPath);
      return from(deleteObject(storageRef));
    } catch (error) {
      return from(Promise.reject(error));
    }
  }

  /**
   * Delete all thumbnails for an entity (cleanup when entity is deleted)
   * @param type The type of thumbnail
   * @param tenantId The tenant ID
   * @param entityId The entity ID
   * @returns Observable that completes when all deletions are done
   */
  deleteAllThumbnailsForEntity(
    type: ThumbnailType,
    tenantId: string,
    entityId: string,
  ): Observable<void> {
    const folderPath = `thumbnails/${type}/${tenantId}/${entityId}`;
    const folderRef = ref(this.storage, folderPath);

    return from(listAll(folderRef)).pipe(
      switchMap((result) => {
        const deletePromises = result.items.map((item) => deleteObject(item));
        return from(Promise.all(deletePromises));
      }),
      switchMap(() => of(void 0)),
    );
  }
}
